import { v7 as uuidv7, validate as uuidValidate } from 'uuid';
import { z } from 'zod';
/**
 * IDs: UUIDv7 en BD (`id uuid`); IDs públicos con prefijo (`public_id text`), p. ej. `pi_01JC9A…`.
 * El público es `<prefijo>_<UUIDv7 en Crockford base32, 26 caracteres>`: ordenable por tiempo y
 * convertible al `uuid` interno sin consultar la BD.
 */
export const ID_PREFIXES = {
    customer: 'cus',
    paymentIntent: 'pi',
    drawdown: 'dd',
    creditLine: 'ln',
    application: 'app',
    document: 'doc',
    dispute: 'dsp',
    agentSession: 'ses',
    event: 'evt',
    authorization: 'au',
    statement: 'st',
    creditPayment: 'pa',
    scaChallenge: 'sca',
    payee: 'pe',
    paymentMethodLink: 'pml',
    incomingRefund: 'irf',
    reconciliationRun: 'rcr',
    reconciliationException: 'rcx',
    humanTask: 'ht',
    offer: 'off',
    webhookSubscription: 'whs',
    webhookDelivery: 'whd',
    agentCard: 'card',
    consoleUser: 'usr',
    oauthClient: 'cli',
    widgetSession: 'wss',
    knowledgeArticle: 'kba',
    memoryFact: 'mf',
    memoryCheckpoint: 'mcp',
    envelope: 'env',
    breBTransfer: 'trf',
    passkey: 'pk',
};
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const CROCKFORD_INDEX = new Map(Array.from({ length: CROCKFORD.length }, (_, i) => [CROCKFORD.charAt(i), i]));
const ENCODED_LENGTH = 26;
export const UuidSchema = z.uuid();
function uuidToBigInt(uuid) {
    return BigInt(`0x${uuid.replace(/-/g, '')}`);
}
function bigIntToUuid(value) {
    const hex = value.toString(16).padStart(32, '0');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export function encodeUuid(uuid) {
    if (!uuidValidate(uuid))
        throw new TypeError(`UUID inválido: ${uuid}`);
    let n = uuidToBigInt(uuid);
    let out = '';
    for (let i = 0; i < ENCODED_LENGTH; i++) {
        out = CROCKFORD.charAt(Number(n & 31n)) + out;
        n >>= 5n;
    }
    return out;
}
export function decodeUuid(encoded) {
    if (encoded.length !== ENCODED_LENGTH)
        throw new TypeError('Identificador codificado inválido');
    let n = 0n;
    for (const char of encoded.toUpperCase()) {
        const v = CROCKFORD_INDEX.get(char);
        if (v === undefined)
            throw new TypeError('Identificador codificado inválido');
        n = (n << 5n) | BigInt(v);
    }
    if (n >> 128n !== 0n)
        throw new TypeError('Identificador codificado inválido');
    return bigIntToUuid(n);
}
/** Nuevo UUIDv7 con la marca de tiempo del `Clock` (respeta el reloj de demo). */
export function newUuid(clock) {
    return uuidv7({ msecs: clock.now().getTime() });
}
export function toPublicId(prefix, uuid) {
    return `${prefix}_${encodeUuid(uuid)}`;
}
export function parsePublicId(publicId) {
    const index = publicId.lastIndexOf('_');
    if (index <= 0)
        throw new TypeError(`ID público inválido: ${publicId}`);
    return { prefix: publicId.slice(0, index), uuid: decodeUuid(publicId.slice(index + 1)) };
}
/** Genera el par `{ id, publicId }` para una entidad nueva. */
export function newId(kind, clock) {
    const id = newUuid(clock);
    return { id, publicId: toPublicId(ID_PREFIXES[kind], id) };
}
export function isPublicId(prefix, value) {
    if (!value.startsWith(`${prefix}_`))
        return false;
    try {
        decodeUuid(value.slice(prefix.length + 1));
        return true;
    }
    catch {
        return false;
    }
}
/** Esquema Zod de un ID público con prefijo. */
export function publicIdSchema(prefix) {
    return z
        .string()
        .regex(new RegExp(`^${prefix}_[0-7][0-9A-HJKMNP-TV-Z]{25}$`), `Debe ser un ID con prefijo ${prefix}_`)
        .meta({
        description: `ID público con prefijo \`${prefix}_\``,
        examples: [`${prefix}_01JC9A7Y3M5Q8R2T4V6W8X0Z1B`],
    });
}
export const CustomerIdSchema = publicIdSchema('cus');
export const PaymentIntentIdSchema = publicIdSchema('pi');
export const DrawdownIdSchema = publicIdSchema('dd');
export const CreditLineIdSchema = publicIdSchema('ln');
export const ApplicationIdSchema = publicIdSchema('app');
export const DocumentIdSchema = publicIdSchema('doc');
export const DisputeIdSchema = publicIdSchema('dsp');
export const AgentSessionIdSchema = publicIdSchema('ses');
export const EventIdSchema = publicIdSchema('evt');
export const AuthorizationIdSchema = publicIdSchema('au');
export const StatementIdSchema = publicIdSchema('st');
export const CreditPaymentIdSchema = publicIdSchema('pa');
export const ScaChallengeIdSchema = publicIdSchema('sca');
export const PasskeyIdSchema = publicIdSchema('pk');
export const PayeeIdSchema = publicIdSchema('pe');
export const IncomingRefundIdSchema = publicIdSchema('irf');
export const HumanTaskIdSchema = publicIdSchema('ht');
export const OfferIdSchema = publicIdSchema('off');
export const WebhookSubscriptionIdSchema = publicIdSchema('whs');
export const WebhookDeliveryIdSchema = publicIdSchema('whd');
export const AgentCardIdSchema = publicIdSchema('card');
export const ReconciliationExceptionIdSchema = publicIdSchema('rcx');
export const ReconciliationRunIdSchema = publicIdSchema('rcr');
export const MemoryCheckpointIdSchema = publicIdSchema('mcp');
export const MemoryFactIdSchema = publicIdSchema('mf');
export const ConsoleUserIdSchema = publicIdSchema('usr');
//# sourceMappingURL=ids.js.map